import React, { useEffect, useMemo } from 'react';
import {
  IMAGE_MODEL_GROUP_LABELS,
  defaultImageModel,
  listImageModelsForUseCase,
  listImageModelsGrouped,
  pickImageModel,
  pickImageModelForUseCase,
  type ImageModelGroup,
  type ImageModelUseCase,
} from '../../lib/imageModelCatalog';
import SelectMenu from '../ui/SelectMenu';

type Props = {
  value: string;
  onChange: (imageModelId: string) => void;
  compact?: boolean;
  disabled?: boolean;
  /** When set, only models tagged for this use-case appear (outpaint vs edit vs recompose…). */
  useCase?: ImageModelUseCase;
  'aria-label'?: string;
};

export default function ImageModelPicker({
  value,
  onChange,
  compact,
  disabled,
  useCase,
  'aria-label': ariaLabel,
}: Props) {
  const models = useMemo(
    () => (useCase ? listImageModelsForUseCase(useCase) : listImageModelsGrouped()),
    [useCase],
  );
  const selected = useMemo(
    () => (useCase ? pickImageModelForUseCase(useCase, value) : pickImageModel(value)),
    [useCase, value],
  );

  useEffect(() => {
    if (!useCase) return;
    if (selected.id !== value) onChange(selected.id);
  }, [useCase, selected.id, value, onChange]);

  const isRecommendedSelected = Boolean(selected.recommended || selected.default);

  const options = models.map((m) => {
    const crown = m.recommended || m.default ? '👑 ' : '';
    const groupKey = (m.group === 'fast' || m.group === 'fidelity' ? m.group : 'recommended') as ImageModelGroup;
    return {
      value: m.id,
      label: m.label,
      menuLabel: `${crown}${m.label}`,
      group: IMAGE_MODEL_GROUP_LABELS[groupKey],
      badge: `${m.creditsPerImage} cr`,
    };
  });

  return (
    <div>
      <SelectMenu
        compact={compact}
        disabled={disabled}
        aria-label={ariaLabel || 'Modelo de imagem'}
        value={selected.id}
        options={options}
        maxHeight="min(360px, 60vh)"
        onChange={onChange}
      />
      {!compact && isRecommendedSelected && (
        <div style={{ marginTop: 4, fontSize: '0.6rem', color: 'rgba(251,191,36,0.85)' }}>
          👑 Mais usado nesta função — default
        </div>
      )}
    </div>
  );
}

export { defaultImageModel, pickImageModel };
